import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { DiscoveryManager } from '../../../managers/discovery.manager';
import { BaseComponent } from '../../base.component';
import { combineLatest, distinctUntilChanged, filter, map, Observable, of, startWith, switchMap, takeUntil } from 'rxjs';
import { ReviewSet } from '../../../models/ui/review-set.model';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ModifyReviewSetDialogComponent } from './create-review-set-dialog/modify-review-set-dialog.component';
import { MatTableDataSource } from '@angular/material/table';
import { UserConfigsService } from '../../../services/user-configs.service';
import { OkCancelDialogComponent } from '../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { NotificationManager } from '../../../managers/notification.manager';
import { MatSort } from '@angular/material/sort';
import { HelpService } from '../../../services/help.service';
import { SplitReviewSetComponent } from './split-review-set/split-review-set.component';
import { selectReviewSetSteps } from '@store/global/global.selectors';
import { Store } from '@ngrx/store';
import { ComparisonUtility } from '@utilities/comparison.utility';

@Component({
    selector: 'lex-review-sets',
    templateUrl: './review-sets.component.html',
    styleUrls: ['./review-sets.component.scss'],
    standalone: false
})
export class ReviewSetsComponent extends BaseComponent implements AfterViewInit {
	@ViewChild(MatSort, { static: false }) sort: MatSort;

	public dataSource$: Observable<MatTableDataSource<ReviewSetViewModel>>;
	public columnsToDisplay = [
		'actions',
		'name',
		'reviewOrder',
		'totalDocuments',
		'reviewedDocuments',
		'remainingDocuments',
		'documentsInReview'
	];

	public urlReviewSetIdTemp: string = '';
	public urlReviewSetIdPermanent: string = '';
	public activeWorkflows: {
		workflowName: string
		workflowKey: string
		executionState: "New" | "Created" | "Started" | "Active" | "OnHold" | "Complete" | "Error" | "Aborted";
	}[];

	private reviewSetViews: ReviewSetViewModel[];

	constructor(
		private discoveryManager: DiscoveryManager,
		private router: Router,
		private route: ActivatedRoute,
		private matDialog: MatDialog,
		private userConfigsService: UserConfigsService,
		private notificationManager: NotificationManager,
		private helpService: HelpService,
		private store: Store) {
		super();
		this.helpService.updateHelpLinkUrl('https://sites.google.com/a/ediscoveryplatform.com/help/home/help/discovery-menu/review-sets');
		this.discoveryManager.refreshReviewSetCollection();
		if (this.userConfigsService.inRoles(this.roles.AccountFeatureSet, this.roles.Administrator)) {
			this.columnsToDisplay.push('manage-actions');
		}

		this.urlReviewSetIdTemp = this.route.snapshot.queryParamMap.get('ReviewSetId');

		this.dataSource$ = combineLatest([
			this.discoveryManager.reviewSets$.pipe(filter(reviewSets => !!reviewSets)),
			this.store.select(selectReviewSetSteps).pipe(
				startWith([])
			)
		]).pipe(
			takeUntil(this.destroy$),
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			map(([reviewSets, workflows]) => {
				const previousActiveWorkflows = this.reviewSetViews?.filter(rs => rs.isReviewSetWorkflowActive).map(rs => rs.id);
				this.reviewSetViews = reviewSets.map(rs => {
					const activeWorkflow = workflows?.find(w => rs.id.toLocaleLowerCase() === w.workflowKey.split('#')[0].toLocaleLowerCase());
					let message = '';
					if (activeWorkflow?.workflowName === 'StartReviewSetWorkflow')
						message = 'Review Set order is currently being set. Please refresh the page when the work is complete.';
					else if (activeWorkflow?.workflowName === 'SplitReviewSetWorkflow')
						message = 'Review Set is currently being split. Please refresh the page when the work is complete.'
					
					return {
						id: rs.id,
						name: rs.name,
						isActive: rs.isActive,
						status: rs.status,
						reviewOrder: rs.reviewOrder,
						totalDocuments: rs.totalDocuments,
						reviewedDocuments: rs.reviewedDocuments,
						remainingDocuments: rs.remainingDocuments,
						documentsInReview: rs.documentsInReview,
						isReviewSetWorkflowActive: !!activeWorkflow,
						toolTipMessage: message 
					}
				});

				const hadWorkflowDeactivate = previousActiveWorkflows?.some(id => {
					const updatedWorkflow = this.reviewSetViews?.find(rs => rs.id === id);
					return updatedWorkflow && !updatedWorkflow.isReviewSetWorkflowActive;
				});

				if (hadWorkflowDeactivate) {
					this.discoveryManager.refreshReviewSetCollection();
				}

				const source = new MatTableDataSource(this.reviewSetViews);
				// Delay setting sort to allow the data source to be initialized.
				setTimeout(() => source.sort = this.sort);
				return source;
			})
		);
	}

	ngAfterViewInit() {
		setTimeout(() => {
			this.urlReviewSetIdPermanent = this.urlReviewSetIdTemp;
			this.urlReviewSetIdTemp = '';
		}, 500)
	}

	public next(item: ReviewSet): void {
		this.discoveryManager.getNextDocument({ reviewSetId: item.id }).subscribe((value) => {
			this.discoveryManager.refreshReviewSetCollection();
			this.router.navigate(['/', 'review', 'doc-viewer'], {
				queryParams: {
					DocId: value,
					ReviewSetId: item.id,
					CaseId: item.caseId
				}
			});
		});
	}

	public exit(item: ReviewSet): void {
		this.discoveryManager.exitReview({ reviewSetId: item.id }).subscribe(() => {
			this.discoveryManager.refreshReviewSetCollection();
		});
	}

	public startReviewSet(item: ReviewSet): void {
		this.discoveryManager.startReviewSet({ reviewSet: item }).subscribe(() => {
			this.discoveryManager.refreshReviewSetCollection();
		});
	}

	public stopReviewSet(item: ReviewSet): void {
		this.discoveryManager.stopReviewSet({ reviewSet: item }).subscribe(() => {
			this.discoveryManager.refreshReviewSetCollection();
		});
	}

	public showAddReviewSetDialog(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		this.matDialog.open(ModifyReviewSetDialogComponent, dialogConfig).afterClosed().subscribe(() => {
			this.discoveryManager.refreshReviewSetCollection();
		});
	}

	public showEditReviewSetDialog(reviewSet: ReviewSet): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = reviewSet;
		this.matDialog.open(ModifyReviewSetDialogComponent, dialogConfig).afterClosed().subscribe(() => {
			this.discoveryManager.refreshReviewSetCollection();
		});
	}

	public delete(item: ReviewSet): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: `Delete Review Set?`,
			message: `Delete Review Set. The Review Set will permanently be deleted from this case. This action cannot be undone.`
		};

		this.matDialog.open(OkCancelDialogComponent, dialogConfig).afterClosed()
			.pipe(
				switchMap((okClicked) => {
					if (okClicked) {
						this.notificationManager.displaySnackBarMessage({ message: 'Review Set Deleted.' });
						return this.discoveryManager.deleteReviewSets({ reviewSet: item });
					}
					return of(null);
				})
			)
			.subscribe(() => {
				this.discoveryManager.refreshReviewSetCollection();
			});
	}

	public showSplitReviewSetDialog(reviewSet: ReviewSet): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = false;
		dialogConfig.data = {
			id: reviewSet.id,
			name: reviewSet.name
		};
		this.matDialog.open(SplitReviewSetComponent, dialogConfig).afterClosed().subscribe(data => {
			if (data?.splitJobStarted) {
				this.notificationManager.displaySnackBarMessage({
					message: 'Split Review set has started. Refresh the page once the work has completed.',
					ttl: 5000
				});
			}
		});
	}
}

interface ReviewSetViewModel {
	id: string;
	name: string;
	isActive: boolean;
	status: 'pre-review' | 'reviewing' | 'post-review';
	reviewOrder: 'master-date' | 'source-file-path';
	totalDocuments: number;
	reviewedDocuments: number;
	remainingDocuments: number;
	documentsInReview: number;
	isReviewSetWorkflowActive: boolean;
	toolTipMessage: string;
}